// RFQ — Request for Quotation: toplu teklif talebi yönetimi.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, ListPageHeader, EmptyState } from '../components/ui';
import { toast } from '../components/Toast';
import { fmtAmount } from '../utils/numberFormat';
import SearchableSelect from '../components/SearchableSelect';

const STATUS_CFG = {
  open:      { l: 'Açık',       c: '#3b82f6' },
  closed:    { l: 'Kapalı',     c: '#64748b' },
  awarded:   { l: 'Verildi',    c: '#10b981' },
  cancelled: { l: 'İptal',      c: '#dc2626' },
};

const RESP_CFG = {
  sent:      { l: 'Gönderildi',  c: '#94a3b8' },
  viewed:    { l: 'Görüldü',     c: '#3b82f6' },
  responded: { l: 'Cevapladı',   c: '#10b981' },
  declined:  { l: 'Reddetti',    c: '#dc2626' },
};

export default function RfqPage({ onNavigate }) {
  const [list, setList] = useState([]);
  const [vendors, setVendors] = useState([]); // suppliers
  const [stocks, setStocks] = useState([]); // RFQ kalemine bağlamak için
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [converting, setConverting] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', due_date: '', due_time: '17:00', currency_code: 'TRY',
    items: [{ description: '', quantity: 1 }],
    vendor_account_ids: [],
  });
  const [showHistory, setShowHistory] = useState(false); // revision history toggle

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/rfq');
      if (r.data.success) {
        const raw = r.data.data;
        setList(Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []));
      }
    } catch (e) { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Tedarikçi + stok listesi
  useEffect(() => {
    // Tedarikçi listesi — accounts paged dönerse defensive parse
    api.get('/accounts', { params: { limit: 500 } })
      .then(r => {
        const raw = r.data?.data;
        const arr = Array.isArray(raw) ? raw
                  : Array.isArray(raw?.items) ? raw.items
                  : Array.isArray(raw?.data) ? raw.data
                  : [];
        const filtered = arr.filter(a => a.type === 'supplier' || a.type === 'both');
        console.log('[RFQ] vendors loaded:', arr.length, 'supplier/both:', filtered.length);
        setVendors(filtered);
      })
      .catch(e => console.error('[RFQ] vendors fetch error:', e.response?.status, e.message));

    // Stoklar — RFQ kalem stok eşleştirmesi için
    api.get('/stocks', { params: { limit: 500 } })
      .then(r => {
        const raw = r.data?.data;
        const arr = Array.isArray(raw) ? raw
                  : Array.isArray(raw?.items) ? raw.items
                  : Array.isArray(raw?.data) ? raw.data
                  : [];
        console.log('[RFQ] stocks loaded:', arr.length);
        setStocks(arr);
      })
      .catch(e => console.error('[RFQ] stocks fetch error:', e.response?.status, e.message));
  }, []);

  const openDetail = async (r) => {
    try {
      const res = await api.get('/rfq/' + r.id);
      if (res.data.success) setSelected(res.data.data);
    } catch (e) { toast.error('Açılamadı'); }
  };

  const createRfq = async () => {
    if (!form.title.trim()) return toast.error('Başlık zorunlu');
    if (!form.items.some(i => i.description?.trim())) return toast.error('En az 1 kalem zorunlu');
    if (!form.vendor_account_ids.length) return toast.error('En az 1 tedarikçi seçin');
    try {
      const r = await api.post('/rfq', {
        ...form,
        items: form.items.filter(i => i.description?.trim()),
      });
      if (r.data.success) {
        toast.success('RFQ oluşturuldu: ' + r.data.data.rfq_number);
        setShowNew(false);
        setForm({ title: '', description: '', due_date: '', due_time: '17:00', currency_code: 'TRY', items: [{ description: '', quantity: 1 }], vendor_account_ids: [] });
        fetchList();
      }
    } catch (e) { toast.error(e.response?.data?.message || 'Hata'); }
  };

  const award = async (vendorAccId) => {
    if (!window.confirm('Bu tedarikçiyi seçtin (award)? RFQ "Verildi" durumuna geçer.')) return;
    try {
      await api.post('/rfq/' + selected.id + '/award', { vendor_account_id: vendorAccId });
      toast.success('İhale verildi');
      const res = await api.get('/rfq/' + selected.id);
      if (res.data.success) setSelected(res.data.data);
      fetchList();
    } catch (e) { toast.error('Hata'); }
  };

  const convertToOrder = async () => {
    if (!selected) return;
    if (!window.confirm(`Direkt "Verilen Sipariş" oluşturulacak. (Tavsiye: önce "Alınan Teklife Aktar" — orada düzenleyip siparişe çevirin)\n\nDevam?`)) return;
    setConverting(true);
    try {
      const r = await api.post('/rfq/' + selected.id + '/convert-to-order');
      if (r.data?.success) {
        let msg = 'Sipariş oluşturuldu: ' + r.data.data.document_number;
        if (r.data.unmatched_count > 0) msg += ` (⚠ ${r.data.unmatched_count} kalem eşleşmedi — notlarda detay)`;
        toast.success(msg);
        const res = await api.get('/rfq/' + selected.id);
        if (res.data.success) setSelected(res.data.data);
        fetchList();
      } else {
        toast.error(r.data?.message || 'Hata');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Dönüşüm başarısız');
    } finally { setConverting(false); }
  };

  const convertToQuote = async () => {
    if (!selected) return;
    if (!window.confirm(`İhale kazanan tedarikçi için "Alınan Teklif" (purchase_quote) oluşturulacak.\n\nSonra Alış → Alınan Teklifler'den açıp düzenleyebilir, depo/KDV ayarlayıp Siparişe çevirebilirsiniz.\n\nDevam?`)) return;
    setConverting(true);
    try {
      const r = await api.post('/rfq/' + selected.id + '/convert-to-quote');
      if (r.data?.success) {
        let msg = 'Alınan Teklif oluşturuldu: ' + r.data.data.document_number;
        if (r.data.auto_matched_count > 0) msg += ` · ${r.data.auto_matched_count} kalem otomatik eşleşti`;
        if (r.data.unmatched_count > 0) msg += ` · ⚠ ${r.data.unmatched_count} kalem eşleşmedi (notlarda detay)`;
        toast.success(msg);
        const res = await api.get('/rfq/' + selected.id);
        if (res.data.success) setSelected(res.data.data);
        fetchList();
      } else {
        const msg = r.data?.message || 'Hata';
        toast.error(msg);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Dönüşüm başarısız');
    } finally { setConverting(false); }
  };

  const openConvertedOrder = () => {
    if (!selected?.converted_order_id || !onNavigate) return;
    onNavigate('orders-outgoing', selected.converted_order_id);
  };

  const openConvertedQuote = () => {
    if (!selected?.converted_quote_id || !onNavigate) return;
    onNavigate('quotes-incoming', selected.converted_quote_id);
  };

  const deleteRfq = async () => {
    if (!selected) return;
    const isConverted = selected.converted_quote_id || selected.converted_order_id;
    const isCancelled = selected.status === 'cancelled';

    // Dönüştürülmüş + henüz iptal değil → önce iptal şart
    if (isConverted && !isCancelled) {
      if (!window.confirm(`Bu RFQ Alınan Teklif/Sipariş'e dönüştürülmüş. Tamamen silmek için önce "İptal" durumuna getirilecek.\n\nİptal edilsin mi? (Sonra tekrar "Sil" diyebilirsiniz)`)) return;
      try {
        await api.post('/rfq/' + selected.id + '/cancel');
        toast.success('RFQ iptal edildi — şimdi tekrar Sil ile kalıcı olarak silebilirsiniz');
        const res = await api.get('/rfq/' + selected.id);
        if (res.data.success) setSelected(res.data.data);
        fetchList();
      } catch (e) { toast.error('Hata'); }
      return;
    }

    // Tamamen sil (cancelled olanları, veya hiç dönüştürülmemiş olanları)
    const warning = isConverted
      ? `İptal edilmiş RFQ ve tüm cevapları kalıcı olarak silinecek.\n\n⚠ Dönüştürdüğünüz Alınan Teklif / Sipariş kayıtları otomatik silinmez — onlar ayrı ayrı yönetilir.\n\nDevam?`
      : `"${selected.title}" RFQ ve tüm tedarikçi cevapları kalıcı olarak silinecek. Geri alınamaz.\n\nDevam?`;
    if (!window.confirm(warning)) return;

    try {
      const r = await api.delete('/rfq/' + selected.id);
      if (r.data?.success) {
        toast.success(r.data.message || 'Silindi');
        setSelected(null);
        fetchList();
      } else {
        toast.error(r.data?.message || 'Silinemedi');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Silinemedi');
    }
  };

  // Karşılaştırma matrisi için yardımcı
  const buildComparisonRows = (rfq) => {
    if (!rfq?.items?.length || !rfq?.vendors?.length) return null;
    const respByVendor = {};
    (rfq.responses || []).forEach(r => {
      if (!respByVendor[r.rfq_vendor_id]) respByVendor[r.rfq_vendor_id] = {};
      respByVendor[r.rfq_vendor_id][r.rfq_item_id] = r;
    });
    return { respByVendor };
  };

  // Tedarikçi başına toplam ve en uygun tedarikçi belirleme
  const computeVendorTotals = (rfq) => {
    const totals = {};
    (rfq.vendors || []).forEach(v => {
      let total = 0;
      let allFilled = true;
      (rfq.items || []).forEach(it => {
        const resp = (rfq.responses || []).find(r => r.rfq_vendor_id === v.id && r.rfq_item_id === it.id);
        if (resp && parseFloat(resp.unit_price) > 0) {
          total += parseFloat(resp.unit_price) * parseFloat(it.quantity);
        } else {
          allFilled = false;
        }
      });
      totals[v.id] = { total, allFilled };
    });
    return totals;
  };

  const copyPublicLink = (token) => {
    const url = window.location.origin + '/rfq-public/' + token;
    navigator.clipboard.writeText(url).then(() => toast.success('Link kopyalandı'));
  };

  return (
    <div className="stocks-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ListPageHeader title="RFQ — Toplu Teklif Talebi" unit="RFQ" count={list.length}
        onRefresh={fetchList} refreshing={loading}
        actions={<Button size="sm" onClick={() => setShowNew(true)}>+ Yeni RFQ</Button>}
      />

      <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
        {!loading && !list.length ? (
          <EmptyState icon="📨" title="Henüz RFQ yok" description='"+ Yeni RFQ" ile birden fazla tedarikçiye aynı anda teklif talebi gönderin. Her tedarikçi kendi linkinden cevabı yazar, sen karşılaştırırsın.' />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
            {list.map(r => {
              const st = STATUS_CFG[r.status] || STATUS_CFG.open;
              return (
                <div key={r.id} style={S.card} onClick={() => openDetail(r)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div>
                      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{r.rfq_number}</div>
                      <strong style={{ fontSize: 'var(--text-md)' }}>{r.title}</strong>
                    </div>
                    <span style={{ ...S.statusBadge, background: st.c + '22', color: st.c }}>{st.l}</span>
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginBottom: 8 }}>
                    📦 {r.item_count} kalem · 👥 {r.vendor_count} tedarikçi · ✓ {r.responded_count} cevap
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {r.due_date && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                        📅 Son: {new Date(r.due_date).toLocaleDateString('tr-TR')}
                        {r.due_time && ' ' + String(r.due_time).substring(11, 16)}
                      </span>
                    )}
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{new Date(r.created_at).toLocaleDateString('tr-TR')}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* NEW RFQ MODAL */}
      {showNew && (
        <Modal title="Yeni RFQ" size="xl" onClose={() => setShowNew(false)}
          footer={
            <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => setShowNew(false)}>İptal</Button>
              <Button onClick={createRfq}>📨 Tedarikçilere Gönder</Button>
            </div>
          }>
          <div style={{ marginBottom: 10, padding: 8, background: 'var(--bg-card-hover)', borderRadius: 'var(--r-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>📦 Stok: <strong style={{ color: stocks.length ? 'var(--success)' : 'var(--danger)' }}>{stocks.length}</strong></span>
            <span>👥 Tedarikçi: <strong style={{ color: vendors.length ? 'var(--success)' : 'var(--danger)' }}>{vendors.length}</strong></span>
            {!vendors.length && <span style={{ color: 'var(--danger)' }}>⚠ Tedarikçi yok — Cariler'den type=supplier veya both ekleyin</span>}
          </div>
          <div className="form-grid">
            <FormField label="Başlık" required>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="örn. 2026 Q3 ofis malzemeleri" />
            </FormField>
            <FormField label="Son Yanıt Tarihi">
              <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
            </FormField>
            <FormField label="Son Yanıt Saati">
              <input type="time" value={form.due_time} onChange={e => setForm({ ...form, due_time: e.target.value })} />
            </FormField>
            <FormField label="Para Birimi">
              <select value={form.currency_code} onChange={e => setForm({ ...form, currency_code: e.target.value })}>
                <option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option>
              </select>
            </FormField>
            <FormField label="Açıklama" fullWidth>
              <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Tedarikçiye iletilecek genel bilgi" />
            </FormField>
          </div>

          <h4 style={{ marginTop: 16, marginBottom: 6 }}>📦 Kalemler</h4>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginBottom: 6 }}>
            💡 <strong>Stok seçerseniz</strong> daha sonra Alınan Teklif / Sipariş dönüşümünde otomatik eşleşir. Boş bırakırsanız ürün adından fuzzy match denenir.
          </div>
          <table style={{ width: '100%', fontSize: 'var(--text-sm)' }}>
            <thead><tr style={{ background: 'var(--bg-tertiary)' }}>
              <th style={{ padding: 6, textAlign: 'left', width: 220 }}>Stok (opsiyonel)</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Açıklama *</th>
              <th style={{ padding: 6, textAlign: 'right', width: 90 }}>Miktar</th>
              <th style={{ width: 30 }}></th>
            </tr></thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={i}>
                  <td style={{ padding: 4 }}>
                    <SearchableSelect
                      value={it.stock_id || ''}
                      onChange={(id) => {
                        const items = [...form.items];
                        items[i].stock_id = id || null;
                        if (id && !items[i].description) {
                          const s = stocks.find(x => x.id === id);
                          if (s) items[i].description = s.name;
                        }
                        setForm({ ...form, items });
                      }}
                      options={stocks}
                      getLabel={(s) => s.name}
                      getSubLabel={(s) => s.code || s.barcode || ''}
                      searchKeys={['name', 'code', 'barcode']}
                      placeholder="— Stok ara —"
                    />
                  </td>
                  <td style={{ padding: 4 }}>
                    <input value={it.description} onChange={e => {
                      const items = [...form.items]; items[i].description = e.target.value; setForm({ ...form, items });
                    }} style={{ width: '100%' }} placeholder="Ürün/hizmet açıklaması" />
                  </td>
                  <td style={{ padding: 4 }}>
                    <input type="number" step="0.01" value={it.quantity} onChange={e => {
                      const items = [...form.items]; items[i].quantity = parseFloat(e.target.value) || 0; setForm({ ...form, items });
                    }} style={{ width: '100%', textAlign: 'right' }} />
                  </td>
                  <td>
                    {form.items.length > 1 && (
                      <button onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button size="xs" variant="ghost" onClick={() => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, stock_id: null }] })}>+ Kalem Ekle</Button>

          <h4 style={{ marginTop: 16, marginBottom: 6 }}>👥 Tedarikçi Seçimi ({form.vendor_account_ids.length})</h4>
          <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 'var(--r-sm)', padding: 8 }}>
            {vendors.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: 12 }}>
                Tedarikçi yok. Cariler sayfasından supplier veya both tipinde cari ekleyin.
              </div>
            ) : vendors.map(v => (
              <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.vendor_account_ids.includes(v.id)}
                  onChange={e => {
                    const ids = e.target.checked
                      ? [...form.vendor_account_ids, v.id]
                      : form.vendor_account_ids.filter(x => x !== v.id);
                    setForm({ ...form, vendor_account_ids: ids });
                  }} />
                <span style={{ fontSize: 'var(--text-sm)' }}>
                  <strong>{v.title}</strong>
                  <span style={{ color: 'var(--text-tertiary)', marginLeft: 6 }}>({v.code}{v.email ? ' · ' + v.email : ''})</span>
                </span>
              </label>
            ))}
          </div>
        </Modal>
      )}

      {/* DETAIL MODAL */}
      {selected && (() => {
        const totals = computeVendorTotals(selected);
        const sortedVendors = [...(selected.vendors || [])].sort((a, b) => {
          const at = totals[a.id]?.total || 0;
          const bt = totals[b.id]?.total || 0;
          if (!at && !bt) return 0;
          if (!at) return 1;
          if (!bt) return -1;
          return at - bt;
        });
        const bestVendorId = sortedVendors.find(v => totals[v.id]?.allFilled)?.id;
        return (
          <Modal title={'RFQ: ' + selected.title} size="xl" onClose={() => setSelected(null)}
            footer={
              <>
                <div className="ui-modal-footer-left" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {(() => {
                    const isCancelled = selected.status === 'cancelled';
                    const isConverted = !!(selected.converted_quote_id || selected.converted_order_id);
                    // Etiket:
                    //   - Cancelled → 🗑 Kalıcı Sil
                    //   - Converted (henüz iptal değil) → ⊘ İptal Et (sonra silinebilir)
                    //   - Normal → 🗑 Sil
                    const label = isCancelled
                      ? '🗑 Kalıcı Sil'
                      : (isConverted ? '⊘ İptal Et' : '🗑 Sil');
                    const title = isCancelled
                      ? 'İptal edilmiş RFQ kalıcı olarak silinir'
                      : (isConverted ? 'Dönüştürülmüş RFQ önce iptal edilir, sonra silinebilir' : 'RFQ ve tüm cevapları silinir');
                    return (
                      <Button variant="danger" onClick={deleteRfq} title={title}>{label}</Button>
                    );
                  })()}
                  {selected.status === 'awarded' && (
                    <>
                      {selected.converted_quote_id ? (
                        <Button variant="success" onClick={openConvertedQuote}>📄 Alınan Teklifi Aç</Button>
                      ) : (
                        <Button variant="success" onClick={convertToQuote} loading={converting}>📄 Alınan Teklife Aktar (Önerilen)</Button>
                      )}
                      {selected.converted_order_id ? (
                        <Button variant="ghost" onClick={openConvertedOrder}>📦 Siparişi Aç</Button>
                      ) : (
                        <Button variant="ghost" onClick={convertToOrder} loading={converting}>📦 Direkt Siparişe</Button>
                      )}
                    </>
                  )}
                </div>
                <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" onClick={() => setSelected(null)}>Kapat</Button>
                </div>
              </>
            }>
            <div style={{ marginBottom: 14, fontSize: 'var(--text-sm)' }}>
              <strong>{selected.rfq_number}</strong> · {selected.description || 'Açıklama yok'}
              {selected.due_date && (
                <div>
                  Son yanıt: {new Date(selected.due_date).toLocaleDateString('tr-TR')}
                  {selected.due_time && ' · ' + String(selected.due_time).substring(11, 16)}
                </div>
              )}
            </div>

            {/* ─── ÖNERİ — fiyat + termin dengesi ─── */}
            {selected.recommendation && selected.recommendation.rankings.length > 1 && (
              <div style={{
                marginBottom: 14, padding: 12, borderRadius: 'var(--r-md)',
                background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(59,130,246,0.05))',
                border: '1px solid var(--accent-primary)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 22 }}>🎯</span>
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--accent-primary)', fontSize: 'var(--text-md)' }}>
                      Önerilen: {selected.recommendation.recommended_vendor_title}
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 2 }}>
                      {selected.recommendation.reason}
                      <span style={{ marginLeft: 8, color: 'var(--text-tertiary)' }}>
                        (skorlama: %70 fiyat + %30 termin)
                      </span>
                    </div>
                  </div>
                </div>
                <table style={{ width: '100%', fontSize: 'var(--text-xs)' }}>
                  <thead><tr style={{ color: 'var(--text-tertiary)' }}>
                    <th style={{ textAlign: 'left', padding: 4 }}>#</th>
                    <th style={{ textAlign: 'left', padding: 4 }}>Tedarikçi</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>Toplam</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>Ort. Termin</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>Skor</th>
                  </tr></thead>
                  <tbody>
                    {selected.recommendation.rankings.map((s, i) => (
                      <tr key={s.vendor_id} style={{ background: i === 0 ? 'rgba(16,185,129,0.1)' : 'transparent' }}>
                        <td style={{ padding: 4, fontWeight: i === 0 ? 700 : 400 }}>{i === 0 ? '🏆' : i + 1}</td>
                        <td style={{ padding: 4, fontWeight: i === 0 ? 700 : 400 }}>{s.vendor_title}</td>
                        <td style={{ padding: 4, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>
                          {fmtAmount(s.total_amount)} {selected.currency_code}
                        </td>
                        <td style={{ padding: 4, textAlign: 'right' }}>
                          {s.avg_lead_time_days ? `${s.avg_lead_time_days} gün` : '—'}
                          {s.max_lead_time_days > s.avg_lead_time_days && <span style={{ color: 'var(--text-tertiary)', fontSize: '0.7rem' }}> (max {s.max_lead_time_days})</span>}
                        </td>
                        <td style={{ padding: 4, textAlign: 'right', fontFamily: 'monospace' }}>
                          {s.score.toFixed(3)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Awarded + converted durumu için banner */}
            {selected.status === 'awarded' && (() => {
              const awardedVendor = selected.vendors?.find(v => v.account_id === selected.awarded_vendor_id);
              const hasAny = selected.converted_quote_id || selected.converted_order_id;
              return (
                <div style={{
                  marginBottom: 14, padding: 12, borderRadius: 'var(--r-md)',
                  background: hasAny ? 'var(--success-bg)' : 'var(--accent-glow)',
                  border: '1px solid ' + (hasAny ? 'var(--success)' : 'var(--accent-primary)'),
                }}>
                  <div style={{ fontWeight: 700, color: hasAny ? 'var(--success)' : 'var(--accent-primary)', marginBottom: 6 }}>
                    {hasAny ? '✓' : '🏆'} İhale verildi: {awardedVendor?.vendor_title || '—'}
                  </div>
                  {!hasAny ? (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      <strong>2 yol var:</strong><br />
                      • <strong>📄 Alınan Teklife Aktar (önerilen):</strong> Önce <strong>Alınan Teklif</strong> oluşur, açıp kalemleri düzenleyebilir (stok eşleştirme, depo, KDV, vade), sonra <strong>quote → order</strong> dönüşümü yaparsın. Daha esnek.<br />
                      • <strong>📦 Direkt Sipariş:</strong> Hızlı yol — taslak <strong>Verilen Sipariş</strong> oluşur, manuel düzenlersin.<br />
                      <em>Stok eşleştirmesi otomatik denenir (ürün adı/kod fuzzy match); eşleşmeyen kalemler notlarda listelenir.</em>
                    </div>
                  ) : (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                      {selected.converted_quote_id && <>📄 Alınan Teklif oluşturuldu — "Alınan Teklifi Aç" ile düzenle. </>}
                      {selected.converted_order_id && <>📦 Verilen Sipariş oluşturuldu.</>}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Karşılaştırma matrisi */}
            <div style={{ overflowX: 'auto', marginBottom: 14 }}>
              <table style={{ minWidth: '100%', fontSize: 'var(--text-xs)', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={{ ...S.th, position: 'sticky', left: 0, background: 'var(--bg-tertiary)' }}>Kalem</th>
                    <th style={S.th}>Miktar</th>
                    {sortedVendors.map(v => (
                      <th key={v.id} style={{ ...S.th, textAlign: 'right', minWidth: 130 }}>
                        <div>{v.vendor_title}</div>
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', fontWeight: 400 }}>
                          {(() => {
                            const rc = RESP_CFG[v.response_status] || RESP_CFG.sent;
                            return <span style={{ color: rc.c }}>● {rc.l}</span>;
                          })()}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selected.items.map(it => (
                    <tr key={it.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                      <td style={{ ...S.td, position: 'sticky', left: 0, background: 'var(--bg-card)' }}>
                        <strong>{it.description}</strong>
                      </td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{it.quantity}</td>
                      {sortedVendors.map(v => {
                        const resp = selected.responses.find(r => r.rfq_vendor_id === v.id && r.rfq_item_id === it.id);
                        // En düşük fiyat highlight
                        const allPrices = sortedVendors
                          .map(vv => selected.responses.find(r => r.rfq_vendor_id === vv.id && r.rfq_item_id === it.id))
                          .filter(r => r && parseFloat(r.unit_price) > 0)
                          .map(r => parseFloat(r.unit_price));
                        const minPrice = allPrices.length ? Math.min(...allPrices) : 0;
                        const isMin = resp && parseFloat(resp.unit_price) === minPrice && minPrice > 0;
                        return (
                          <td key={v.id} style={{ ...S.td, textAlign: 'right', background: isMin ? 'var(--success-bg)' : 'transparent', fontWeight: isMin ? 700 : 400 }}>
                            {resp?.unit_price ? fmtAmount(resp.unit_price) + ' ' + (selected.currency_code || 'TRY') : '—'}
                            {resp?.lead_time_days && <div style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)' }}>{resp.lead_time_days} gün</div>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--bg-secondary)', borderTop: '2px solid var(--accent-primary)', fontWeight: 700 }}>
                    <td style={{ ...S.td, position: 'sticky', left: 0, background: 'var(--bg-secondary)' }}>TOPLAM</td>
                    <td style={S.td}></td>
                    {sortedVendors.map(v => {
                      const t = totals[v.id]?.total || 0;
                      const isBest = v.id === bestVendorId;
                      return (
                        <td key={v.id} style={{ ...S.td, textAlign: 'right', background: isBest ? 'var(--success-bg)' : 'transparent', color: isBest ? 'var(--success)' : 'inherit' }}>
                          {t > 0 ? fmtAmount(t) + ' ' + (selected.currency_code || 'TRY') : '—'}
                          {isBest && <div style={{ fontSize: '0.6rem' }}>🏆 EN UYGUN</div>}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Aksiyonlar — yanıt zamanı + revision info */}
            <h4 style={{ marginTop: 14, marginBottom: 8 }}>Tedarikçiler</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sortedVendors.map(v => {
                const t = totals[v.id];
                const rc = RESP_CFG[v.response_status] || RESP_CFG.sent;
                const respondedDate = v.responded_at ? new Date(v.responded_at) : null;
                const modifiedDate  = v.last_modified_at ? new Date(v.last_modified_at) : null;
                const fmtDT = (d) => d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                return (
                  <div key={v.id} style={{ padding: 8, background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 'var(--r-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ flex: 1 }}>
                        <strong>{v.vendor_title}</strong>
                        <span style={{ marginLeft: 6, color: rc.c, fontSize: 'var(--text-xs)' }}>● {rc.l}</span>
                        {t?.total > 0 && <span style={{ marginLeft: 8, fontWeight: 700, color: 'var(--accent-primary)' }}>{fmtAmount(t.total)} {selected.currency_code}</span>}
                      </span>
                      <Button size="xs" variant="ghost" onClick={() => copyPublicLink(v.token)}>🔗 Link</Button>
                      {selected.status === 'open' && v.response_status === 'responded' && (
                        <Button size="xs" onClick={() => award(v.account_id)}>🏆 İhaleyi Ver</Button>
                      )}
                    </div>
                    {(respondedDate || modifiedDate) && (
                      <div style={{ marginTop: 6, fontSize: '0.7rem', color: 'var(--text-tertiary)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {respondedDate && <span>📩 İlk yanıt: <strong>{fmtDT(respondedDate)}</strong></span>}
                        {modifiedDate && (
                          <span style={{ color: 'var(--warning)' }}>
                            ✏️ Son düzeltme: <strong>{fmtDT(modifiedDate)}</strong>
                            {v.revision_count > 0 && ` (${v.revision_count} kez güncellendi)`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ─── REVIZYON GEÇMİŞİ — collapsible ─── */}
            {selected.history && selected.history.length > 0 && (
              <div style={{ marginTop: 14, padding: 10, background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 'var(--r-sm)' }}>
                <div
                  onClick={() => setShowHistory(!showHistory)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 'var(--text-sm)' }}
                >
                  <span>{showHistory ? '▼' : '▶'}</span>
                  <span>📜 Revizyon Geçmişi ({selected.history.length} değişiklik)</span>
                </div>
                {showHistory && (
                  <table style={{ width: '100%', marginTop: 10, fontSize: 'var(--text-xs)' }}>
                    <thead>
                      <tr style={{ color: 'var(--text-tertiary)' }}>
                        <th style={{ textAlign: 'left', padding: 4 }}>Tarih/Saat</th>
                        <th style={{ textAlign: 'left', padding: 4 }}>Tedarikçi</th>
                        <th style={{ textAlign: 'left', padding: 4 }}>Alan</th>
                        <th style={{ textAlign: 'right', padding: 4 }}>Eski</th>
                        <th style={{ textAlign: 'right', padding: 4 }}>Yeni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.history.map(h => {
                        const fieldLabels = {
                          unit_price: 'Birim Fiyat',
                          lead_time_days: 'Termin (gün)',
                          notes: 'Kalem Notu',
                          total_amount: 'Toplam Tutar',
                          header_notes: 'Genel Not',
                        };
                        const itemRow = h.rfq_item_id ? selected.items.find(it => it.id === h.rfq_item_id) : null;
                        const dt = new Date(h.changed_at);
                        return (
                          <tr key={h.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                            <td style={{ padding: 4, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                              {dt.toLocaleDateString('tr-TR')} {dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td style={{ padding: 4 }}>{h.vendor_title}</td>
                            <td style={{ padding: 4 }}>
                              <strong>{fieldLabels[h.field_name] || h.field_name}</strong>
                              {itemRow && <div style={{ color: 'var(--text-tertiary)', fontSize: '0.65rem' }}>{itemRow.description}</div>}
                            </td>
                            <td style={{ padding: 4, textAlign: 'right', color: 'var(--danger)', textDecoration: 'line-through' }}>
                              {h.old_value || '—'}
                            </td>
                            <td style={{ padding: 4, textAlign: 'right', color: 'var(--success)', fontWeight: 700 }}>
                              {h.new_value || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </Modal>
        );
      })()}
    </div>
  );
}

const S = {
  card: {
    padding: 14,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    cursor: 'pointer',
    boxShadow: 'var(--shadow-xs)',
  },
  statusBadge: { fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--r-full)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  th: { padding: '8px 10px', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '8px 10px' },
};
